// src/pages/Task/RecentlyDeleted.tsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Trash2, RotateCcw, AlertTriangle, Clock, Search,
  Calendar, Sparkles, ShieldAlert, Check, Minus, ListChecks, X,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { tasksApi } from "../../api/tasks";
import { useAuth } from "../../contexts/AuthContext";

interface DeletedTask {
  id: string;
  title: string;
  description?: string | null;
  status: string;
  priority: string;
  due_date?: string | null;
  deleted_at?: string | null;
  deleted_by?: string | null;
  created_by?: string | null;
  primary_assignee_id?: string | null;
  client_id?: string | null;
  progress_percent?: number;
}

const PRIORITY_PILLS: Record<string, string> = {
  URGENT: "gt-pill is-urgent",
  HIGH:   "gt-pill is-high",
  MEDIUM: "gt-pill is-medium",
  LOW:    "gt-pill is-low",
};

// Roles that may permanently delete: "manager and above". Must match the backend
// CAN_PERMANENT_DELETE_ROLES (utils/task_permissions.py). Drives the Delete-forever
// button via canPurge. Supervisor (view-only) and employee are excluded.
const ELEVATED = new Set([
  "manager", "hr", "hr head", "hr admin", "hod",
  "admin", "owner", "superadmin", "director", "founder",
]);

/** Premium round selection control — gradient-filled with a check when on,
 *  a dash when indeterminate, a hairline ring when off. Replaces native checkboxes. */
function RoundCheck({
  checked,
  indeterminate = false,
  onChange,
  disabled = false,
  label,
  size = 22,
  style,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onChange: () => void;
  disabled?: boolean;
  label: string;
  size?: number;
  style?: React.CSSProperties;
}) {
  const active = checked || indeterminate;
  const [focused, setFocused] = useState(false);
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={indeterminate ? "mixed" : checked}
      aria-label={label}
      disabled={disabled}
      onClick={(e) => { e.stopPropagation(); onChange(); }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      className="gt-round-check"
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 0,
        cursor: disabled ? "not-allowed" : "pointer",
        border: active ? "2px solid transparent" : "2px solid var(--gt-border-strong)",
        background: active
          ? "linear-gradient(135deg, var(--gt-accent-strong), var(--gt-primary-deep, var(--gt-accent-strong)))"
          : "var(--gt-surface-0)",
        color: "#fff",
        boxShadow: focused
          ? "0 0 0 3px var(--gt-primary-container)"
          : active ? "0 2px 8px -2px var(--gt-accent-strong)" : "none",
        transition: "background 160ms ease, border-color 160ms ease, box-shadow 160ms ease, transform 120ms ease",
        opacity: disabled ? 0.5 : 1,
        ...style,
      }}
    >
      {indeterminate ? (
        <Minus style={{ width: size * 0.6, height: size * 0.6 }} strokeWidth={3.5} />
      ) : checked ? (
        <Check style={{ width: size * 0.6, height: size * 0.6 }} strokeWidth={3.5} />
      ) : null}
    </button>
  );
}

export default function RecentlyDeleted() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = ELEVATED.has((user?.role || "").toLowerCase());
  // Permanent delete is gated to manager-and-above (ELEVATED); the backend enforces
  // this too. (The messenger copy of this file sets `const canPurge = true;` since its
  // chat-auth role never resolves reliably — keep that divergence when syncing.)
  const canPurge = isAdmin;

  const [items, setItems] = useState<DeletedTask[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectMode, setSelectMode] = useState(false);
  const [confirm, setConfirm] = useState<{ ids: string[]; mode: "restore" | "purge" } | null>(null);
  const [busy, setBusy] = useState(false);

  const exitSelect = () => { setSelectMode(false); setSelected(new Set()); setConfirm(null); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await tasksApi.listRecentlyDeleted({ days, page: 1, page_size: 100 });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch {
      /* silent on load failure; show empty state */
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return items;
    return items.filter(t =>
      t.title.toLowerCase().includes(q) ||
      (t.description || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  // Keep the selection pruned to what's actually VISIBLE (respects the search +
  // day-range filter). Prevents bulk Restore / Delete-forever from silently acting
  // on items hidden by the current filter — the count always matches what's on screen.
  useEffect(() => {
    setSelected(prev => {
      if (prev.size === 0) return prev;
      const ids = new Set(filtered.map(t => t.id));
      let changed = false;
      const next = new Set<string>();
      prev.forEach(id => { if (ids.has(id)) next.add(id); else changed = true; });
      return changed ? next : prev;
    });
  }, [filtered]);

  const visibleIds = useMemo(() => filtered.map(t => t.id), [filtered]);
  const allSelected = visibleIds.length > 0 && visibleIds.every(id => selected.has(id));
  const someSelected = selected.size > 0;

  const toggleOne = (id: string) =>
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const toggleAll = () =>
    setSelected(prev => (visibleIds.every(id => prev.has(id)) ? new Set() : new Set(visibleIds)));

  const runAction = async (ids: string[], mode: "restore" | "purge") => {
    if (!ids.length) return;
    setBusy(true);
    const call = mode === "restore" ? tasksApi.restore : tasksApi.permanentDelete;
    try {
      const results = await Promise.allSettled(ids.map(id => call(id)));
      const ok: string[] = [];
      let failed = 0;
      let failDetail = "";
      results.forEach((r, i) => {
        if (r.status === "fulfilled") {
          ok.push(ids[i]);
        } else {
          failed += 1;
          const detail = (r.reason as any)?.response?.data?.detail;
          if (detail && !failDetail) failDetail = String(detail);
        }
      });
      if (ok.length) {
        const okSet = new Set(ok);
        setItems(prev => prev.filter(t => !okSet.has(t.id)));
        setTotal(n => Math.max(0, n - ok.length));
        setSelected(prev => {
          const next = new Set(prev);
          ok.forEach(id => next.delete(id));
          return next;
        });
      }
      const verb = mode === "restore" ? "restored" : "permanently deleted";
      if (failed === 0) {
        toast.success(ids.length > 1 ? `${ok.length} tasks ${verb}` : `Task ${verb}`);
      } else if (ok.length) {
        toast.warning(`${ok.length} ${verb}, ${failed} failed${failDetail ? ` — ${failDetail}` : ""}`);
      } else {
        toast.error(failDetail || `Could not ${mode === "restore" ? "restore" : "delete"} ${ids.length > 1 ? "tasks" : "task"}`);
      }
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  return (
    <div className="gt-shell min-h-screen">
      <div className="max-w-6xl mx-auto p-4 md:p-7 space-y-6 gt-fade-in">
        <button onClick={() => navigate(-1)} className="gt-btn is-ghost is-sm">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        {/* Header */}
        <div className="gt-page-hero flex-col md:flex-row gap-4">
          <div>
            <h1 className="gt-page-title">
              Recently <em>Deleted</em>
            </h1>
            <p className="gt-page-sub" style={{ marginTop: 6 }}>
              Tasks soft-deleted in the last <em className="gt-serif-italic">{days}</em> days. Restore or remove for good.
            </p>
          </div>

          <div className="flex items-end gap-3 flex-wrap">
            <div className="gt-search-wrap" style={{ width: 240 }}>
              <Search />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search trash…"
                className="gt-input is-search"
              />
            </div>
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="gt-select"
              style={{ width: 'auto' }}
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={180}>Last 6 months</option>
              <option value={365}>Last year</option>
            </select>
          </div>
        </div>

        {/* Counter / bulk action strip */}
        <div className="gt-card p-4 flex items-center gap-3 flex-wrap">
          {selectMode && filtered.length > 0 && (
            <RoundCheck
              checked={allSelected}
              indeterminate={someSelected && !allSelected}
              onChange={toggleAll}
              disabled={busy}
              label="Select all tasks"
            />
          )}
          <span style={{
            width: 36, height: 36, borderRadius: 12,
            background: 'var(--gt-status-overdue-bg)', color: 'var(--gt-prio-urgent-fg)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Trash2 className="w-4 h-4" />
          </span>
          <div className="flex-1 min-w-0">
            <span className="gt-eyebrow" style={{ marginBottom: 0, fontSize: 10 }}>{selectMode && someSelected ? "Selected" : "In trash"}</span>
            <p className="gt-display gt-numerals" style={{ fontSize: 18, fontWeight: 800, color: 'var(--gt-text-primary)' }}>
              {selectMode && someSelected ? `${selected.size} selected` : `${total} task${total !== 1 ? "s" : ""}`}
            </p>
          </div>

          {selectMode ? (
            <div className="flex items-center gap-2 flex-wrap">
              {someSelected && (
                <>
                  <button
                    onClick={() => setConfirm({ ids: Array.from(selected), mode: 'restore' })}
                    className="gt-btn is-soft is-sm"
                    disabled={busy}
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    Restore ({selected.size})
                  </button>
                  {canPurge && (
                    <button
                      onClick={() => setConfirm({ ids: Array.from(selected), mode: 'purge' })}
                      className="gt-btn is-danger is-sm"
                      disabled={busy}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Delete forever ({selected.size})
                    </button>
                  )}
                </>
              )}
              <button
                onClick={exitSelect}
                className="gt-btn is-ghost is-sm"
                disabled={busy}
              >
                <X className="w-3.5 h-3.5" />
                Done
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              {!canPurge && (
                <span className="gt-pill is-ghost">
                  <ShieldAlert className="w-3 h-3" /> Only managers &amp; above can delete
                </span>
              )}
              {filtered.length > 0 && (
                <button
                  onClick={() => setSelectMode(true)}
                  className="gt-btn is-soft is-sm"
                  disabled={busy}
                >
                  <ListChecks className="w-3.5 h-3.5" />
                  Select
                </button>
              )}
            </div>
          )}
        </div>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-20"><div className="gt-loading-ring" /></div>
        ) : filtered.length === 0 ? (
          <div className="gt-card p-12">
            <div className="gt-empty">
              <div className="gt-empty-leaf"><Sparkles className="w-6 h-6" /></div>
              <div className="gt-empty-title">Trash is empty</div>
              <div className="gt-page-sub">
                {search ? <>No deleted tasks match <em className="gt-serif-italic">"{search}"</em>.</> : <>No tasks have been deleted in this <em className="gt-serif-italic">window</em>.</>}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-2.5">
            <AnimatePresence>
              {filtered.map((t) => (
                <motion.div
                  key={t.id}
                  layout
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className={`gt-task-card is-prio-${(t.priority || 'medium').toLowerCase()} p-4 pl-5 group`}
                  style={{
                    minHeight: 'auto',
                    ...(selected.has(t.id)
                      ? { borderColor: 'var(--gt-accent-strong)', boxShadow: '0 0 0 2px var(--gt-primary-container)' }
                      : {}),
                  }}
                >
                  <div className={`gt-prio-bar is-${(t.priority || 'medium').toLowerCase()}`} />

                  <div className="flex items-start gap-4">
                    {selectMode && (
                      <RoundCheck
                        checked={selected.has(t.id)}
                        onChange={() => toggleOne(t.id)}
                        disabled={busy}
                        label={`Select ${t.title}`}
                        style={{ marginTop: 2 }}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="gt-display truncate" style={{ fontSize: 15, fontWeight: 700, color: 'var(--gt-text-primary)' }}>
                        {t.title}
                      </h3>
                      {t.description && (
                        <p className="line-clamp-1" style={{ fontSize: 12.5, color: 'var(--gt-text-tertiary)', marginTop: 4 }}>
                          {t.description}
                        </p>
                      )}

                      <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
                        <span className={PRIORITY_PILLS[t.priority] || 'gt-pill is-medium'}>
                          <span className="gt-dot" />
                          {t.priority}
                        </span>
                        <span className="gt-pill is-pending">
                          {t.status.replace(/_/g, ' ')}
                        </span>
                        {t.due_date && (
                          <span className="gt-pill is-ghost gt-numerals">
                            <Calendar className="w-3 h-3" />
                            {format(new Date(t.due_date), "MMM d, yyyy")}
                          </span>
                        )}
                        {t.deleted_at && (
                          <span className="gt-pill is-overdue gt-numerals">
                            <Clock className="w-3 h-3" />
                            Deleted {format(new Date(t.deleted_at), "MMM d, yyyy · h:mm a")}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => setConfirm({ ids: [t.id], mode: 'restore' })}
                        className="gt-btn is-soft is-sm"
                        disabled={busy}
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        Restore
                      </button>
                      {canPurge && (
                        <button
                          onClick={() => setConfirm({ ids: [t.id], mode: 'purge' })}
                          className="gt-btn is-danger is-sm"
                          disabled={busy}
                          title="Permanently delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Delete forever
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Confirm modal */}
      {confirm && (
        <div className="gt-modal-backdrop flex items-center justify-center p-4" onClick={() => !busy && setConfirm(null)}>
          <div className="gt-modal w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-6" style={{ borderBottom: '1px solid var(--gt-divider)' }}>
              <div className="flex items-center gap-3">
                <span style={{
                  width: 44, height: 44, borderRadius: 14,
                  background: confirm.mode === 'restore' ? 'var(--gt-primary-container)' : 'var(--gt-status-overdue-bg)',
                  color: confirm.mode === 'restore' ? 'var(--gt-primary-deep)' : 'var(--gt-prio-urgent-fg)',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {confirm.mode === 'restore' ? <RotateCcw className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
                </span>
                <h3 className="gt-display" style={{ fontSize: 18 }}>
                  {confirm.mode === 'restore'
                    ? <>Restore <em className="gt-serif-italic" style={{ color: 'var(--gt-accent-strong)' }}>Task{confirm.ids.length > 1 ? 's' : ''}</em>?</>
                    : <>Delete <em className="gt-serif-italic" style={{ color: 'var(--gt-prio-urgent-fg)' }}>Forever</em>?</>}
                </h3>
              </div>
            </div>
            <div className="p-6">
              <p className="gt-page-sub">
                {confirm.mode === 'restore'
                  ? <>{confirm.ids.length > 1 ? `${confirm.ids.length} tasks` : 'The task'} will be restored to your active list and visible again to everyone with access.</>
                  : <><span style={{ color: 'var(--gt-prio-urgent-fg)' }}>This cannot be undone.</span> {confirm.ids.length > 1 ? `These ${confirm.ids.length} tasks` : 'The task'} and all {confirm.ids.length > 1 ? 'their' : 'its'} history will be removed permanently.</>}
              </p>
            </div>
            <div className="p-6 flex justify-end gap-3" style={{ borderTop: '1px solid var(--gt-divider)' }}>
              <button onClick={() => setConfirm(null)} className="gt-btn is-ghost" disabled={busy}>Cancel</button>
              <button
                onClick={() => runAction(confirm.ids, confirm.mode)}
                className={confirm.mode === 'restore' ? 'gt-btn is-primary' : 'gt-btn is-danger'}
                disabled={busy}
              >
                {busy ? '…' : (confirm.mode === 'restore' ? 'Restore' : 'Delete forever')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
